import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = process.env.SUPABASE_URL || 'https://ev.tpkmedia.info/sb';
const supabaseAnonKey =
  process.env.SUPABASE_ANON_KEY ||
  'eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJzdXBhYmFzZSIsImlhdCI6MTc2MjMxOTY1MSwiZXhwIjoyMDc3ODUyNDUxLCJhdWQiOiIiLCJzdWIiOiJhbm9uIiwicm9sZSI6ImFub24ifQ.G0DpkTJncADlKRpIJACD8aLIMqfD9dohS_zTl7L9cb0';

// Create Supabase client
let supabaseClient: SupabaseClient | null = null;

export const getSupabaseClient = (): SupabaseClient => {
  if (!supabaseClient) {
    if (!supabaseUrl || !supabaseAnonKey) {
      console.error('Missing Supabase credentials. Please set SUPABASE_URL and SUPABASE_ANON_KEY in your environment variables.');
    }
    supabaseClient = createClient(supabaseUrl, supabaseAnonKey);
  }
  return supabaseClient;
};

// Auth helpers
export const SupabaseAuth = {
  // Sign in with email and password
  signIn: async (email: string, password: string) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  },

  // Sign up with email and password
  signUp: async (email: string, password: string, metadata?: any) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
      },
    });
    return { data, error };
  },

  // Admin: Create user with auto-confirm (bypasses email confirmation)
  adminCreateUser: async (email: string, password: string, metadata?: any, autoConfirm: boolean = true) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.admin.createUser({
      email,
      password,
      email_confirm: autoConfirm,
      user_metadata: metadata,
    });
    return { data, error };
  },

  // Sign out
  signOut: async () => {
    const supabase = getSupabaseClient();
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current user
  getCurrentUser: async () => {
    const supabase = getSupabaseClient();
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  // Get session
  getSession: async () => {
    const supabase = getSupabaseClient();
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  },

  // Reset password
  resetPassword: async (email: string) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.resetPasswordForEmail(email);
    return { data, error };
  },

  // Update user
  updateUser: async (attributes: any) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.updateUser(attributes);
    return { data, error };
  },

  // Send password reset OTP
  sendPasswordResetOTP: async (email: string) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/forgot-password',
    });
    return { data, error };
  },

  // Verify OTP for password reset
  verifyPasswordResetOTP: async (email: string, token: string) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'recovery',
    });
    return { data, error };
  },

  // Update password
  updatePassword: async (newPassword: string) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { data, error };
  },

  // Listen to auth state changes
  onAuthStateChange: (callback: (event: string, session: any) => void) => {
    const supabase = getSupabaseClient();
    return supabase.auth.onAuthStateChange(callback);
  },
};

// Database helpers
export const SupabaseDB = {
  // Generic select query
  select: async <T = any>(table: string, columns = '*', filters?: any) => {
    const supabase = getSupabaseClient();
    let query = supabase.from(table).select(columns);
    
    if (filters) {
      Object.keys(filters).forEach(key => {
        query = query.eq(key, filters[key]);
      });
    }
    
    const { data, error } = await query;
    return { data: data as T[], error };
  },

  // Insert data
  insert: async <T = any>(table: string, data: any) => {
    const supabase = getSupabaseClient();
    const { data: result, error } = await supabase
      .from(table)
      .insert(data)
      .select();
    return { data: result as T[], error };
  },

  // Update data
  update: async <T = any>(table: string, id: any, data: any, idColumn = 'id') => {
    const supabase = getSupabaseClient();
    const { data: result, error } = await supabase
      .from(table)
      .update(data)
      .eq(idColumn, id)
      .select();
    return { data: result as T[], error };
  },

  // Delete data
  delete: async (table: string, id: any, idColumn = 'id') => {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from(table)
      .delete()
      .eq(idColumn, id);
    return { error };
  },

  // Custom query
  customQuery: (table: string) => {
    const supabase = getSupabaseClient();
    return supabase.from(table);
  },
};

// Storage helpers
export const SupabaseStorage = {
  // Upload file
  uploadFile: async (bucket: string, path: string, file: File) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file);
    return { data, error };
  },

  // Download file
  downloadFile: async (bucket: string, path: string) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.storage
      .from(bucket)
      .download(path);
    return { data, error };
  },

  // Get public URL
  getPublicUrl: (bucket: string, path: string) => {
    const supabase = getSupabaseClient();
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);
    return data.publicUrl;
  },

  // Delete file
  deleteFile: async (bucket: string, paths: string[]) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.storage
      .from(bucket)
      .remove(paths);
    return { data, error };
  },

  // List files
  listFiles: async (bucket: string, path?: string) => {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.storage
      .from(bucket)
      .list(path);
    return { data, error };
  },
};

export default getSupabaseClient;
